import { TreeNode } from '@shared/ui-native/Tree';
import * as olExtent from 'ol/extent';
import { ModelsLayer } from './ModelsLayer';
import { IModelInfo } from '@shared/models/types';
import { LoadingManager } from '@shared/models/loading-manager';
import { Auth } from '@shared/Auth';
import Static from 'ol/source/ImageStatic';
import ImageLayer from 'ol/layer/Image';
import Feature from 'ol/Feature';
import { StructureLayer } from './StructureLayer';
import { RateLimiter } from '@shared/utils/rate-limiter';

// New: same origin, proxied through kuura — single uuid
const MODEL_API_BASE = '/kuura/v1/viz/model/';

// Old: direct microservice — project + file uuid
// const MODEL_API_BASE = `${kuura.AwsApiGatewayDomain()}/viz/v1/`;

const modelUrl = (uuid: string, endpoint: string): string =>
    `${MODEL_API_BASE}${uuid}/${endpoint}`;

// Old microservice variant (uncomment with old MODEL_API_BASE):
// const modelUrl = (projectUuid: string, fileUuid: string, endpoint: string): string =>
//     `${MODEL_API_BASE}${projectUuid}/${fileUuid}/${endpoint}`;

const outlineLimiter = new RateLimiter(3);

export class OutlineModel {
    uuid: string;
    node: TreeNode;
    extent: olExtent.Extent;

    meshImageLayer: ImageLayer<Static>;

    hitDetectionFeature: Feature;
    structureLayer: StructureLayer;
    modelsLayer: ModelsLayer;

    modelInfo: IModelInfo;

    originalNodeText: string;

    constructor(node: TreeNode, structureLayer: StructureLayer, modelsLayer: ModelsLayer) {
        this.uuid = node.uuid;
        this.node = node;
        this.structureLayer = structureLayer;
        this.modelsLayer = modelsLayer;
        this.extent = olExtent.createEmpty();
        this.originalNodeText = node.text;
    }

    public async load() {
        await this.loadOutlines();
        this.node.setLoading(false);
    }

    public dispose() {
        if (this.meshImageLayer) {
            this.structureLayer.removeLayer(this.meshImageLayer);
            this.meshImageLayer.setSource(null);
            this.meshImageLayer = null;
        }

        this.structureLayer.removeHitDetectionFeature(this.hitDetectionFeature);
        this.modelsLayer.removeFeaturesBySource({ modelId: this.node.id });
    }

    private async loadOutlines() {
        LoadingManager.startLoading(this.uuid);

        const releaseToken = await outlineLimiter.requestToken();
        try {
            const token = await Auth.getToken();

            const url = modelUrl(this.uuid, 'outlines');

            const maxRetries = 3;
            let response: Response;

            const headers = await Auth.getHeaders();
            for (let attempt = 0; attempt < maxRetries; attempt++) {
                try {
                    response = await fetch(url, {
                        headers,
                    });
                    console.log(`[outline] attempt ${attempt + 1} status: ${response.status}`);
                    if (response.ok || response.status < 500) break;
                } catch (fetchError) {
                    console.log(`[outline] attempt ${attempt + 1} fetch threw:`, fetchError);
                    if (attempt === maxRetries - 1) throw fetchError;
                }
                if (attempt < maxRetries - 1) {
                    await new Promise(resolve => setTimeout(resolve, 2000 * Math.pow(2, attempt)));
                }
            }

            if (!response.ok) {
                console.error(`Failed to load outline: ${response.status}`);
                this.node.setError("Failed to load outline");
                this.node.setChecked(0, true, false);
                return;
            }

            const outlineResponse = await response.json();

            const { features } = this.modelsLayer.drawGeoJSON(this.node.id, this.node.text, outlineResponse);
            // get bounds of the features
            const bounds = olExtent.createEmpty();
            for (const feature of features) {
                olExtent.extend(bounds, feature.getGeometry().getExtent());
            }

            this.extent = bounds;
        } catch (e) {
            console.error(e);
            this.node.setError("Failed to load outline");
            this.node.setChecked(0, true, false);
        } finally {
            releaseToken();
            LoadingManager.finishLoading(this.uuid);
        }
    }

    setOpacity(solid: number, line: number) {
        const solidSafe = utils.clamp(0.04, solid / 100, 1);

        if (this.structureLayer) this.structureLayer.setOpacity(solidSafe);
    }
}
